import React from "react";
import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import { useAvivaSelector } from "@redux/hook";
import { useTheme, ITheme, themeDefault } from "src/theme-revamp";
import CalculatingLoader from "@components/_Revamp/CalculatingLoader";
import { PLANS_NAME } from "@static/config";
import { SCREEN_SIZE } from "@static/screen-size";

const ListingLayout = styled.div<Partial<ListingProps>>`
  margin: ${(props) => (props.margin ? props.margin : "0")};
  height: auto;

  @media screen and (max-height: 625px) {
    scrollbar-width: none;
    -ms-overflow-style: none;
    overflow: -moz-scrollbars-none;
    overflow-y: scroll;
    max-height: 450px;
    &::-webkit-scrollbar {
      display: none; /* Safari and Chrome */
    }
  }
`;

const ListingItemDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  @media screen and (min-width: 768px) {
    row-gap: 26px;
  }
`;

const ListingDidiver = styled.div<DividerProps>`
  background-color: ${(props) =>
    props.dividerBackground ? props.dividerBackground : "#C4C4C4"};
  height: 1px;
  margin-top: ${(props) =>
    props.dividerMarginTop ? props.dividerMarginTop : "48px"};
  margin-bottom: ${(props) =>
    props.dividerMarginBottom ? props.dividerMarginBottom : "48px"};
`;

const SummaryText = styled.h4<SummaryTextProps>`
  color: ${(props) =>
    props.summaryTextColor ? props.summaryTextColor : "#883e89"};
  font-size: ${(props) => (props.fontSize ? props.fontSize : "20px")};
  font-weight: 600;
  line-height: 1.4;
`;

const ListingDescriptionText = styled.p`
  color: #434343;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  width: 50%;
  max-width: 350px;
  a {
    color: #883e89;
    font-weight: 600;
    cursor: pointer;
  }

  @media only screen and (max-width: ${SCREEN_SIZE.mobilesUp}px) {
    width: 80%;
  }
`;

const ListingDescriptionValue = styled.p<Pick<ListingProps, "variant">>`
  color: #434343;
  font-size: 14px;
  font-weight: ${(props) =>
    props.variant === "summarization" ? "600" : "400"};
  line-height: 1.4;
  text-align: right;
  @media only screen and (max-width: ${SCREEN_SIZE.mobilesUp}px) {
    white-space: nowrap;
  }
`;

const ListingItemContainer = styled.div<
  Pick<ListingProps, "variant" | "underlineColor">
>`
  display: flex;
  flex-direction: column;
  row-gap: 3px;
  //${(props) => (props.variant === "summarization" ? "16px" : "3px")};
  padding: ${(props) => (props.variant === "alternate" ? "24px" : "0")};
  border-bottom: ${(props) =>
    props.variant === "alternate"
      ? "1px solid" + (props.underlineColor ? props.underlineColor : "#C4C4C4")
      : "none"};
  &:nth-last-child(1) {
    border-bottom: none;
  }
`;

const ListingHeadingText = styled.p<
  Pick<ListingProps, "variant" | "headingFontSize">
>`
  color: #434343;
  font-size: ${(props) =>
    props.variant === "summarization" ? "20px" : "14px"};
  font-weight: 600;
  line-height: 1.4;
  margin-right: 20px;
  flex: 1;
  @media screen and (max-width: 768px) {
  }
`;

const ListingHeadingValue = styled.div<
  Pick<ListingProps, "variant" | "headerMaxValueWidth">
>`
  position: relative;
  color: #434343;
  font-size: 14px;
  font-weight: ${(props) => (props.variant === "alternate" ? "400" : "600")};
  line-height: 1.4;
  text-align: right;
  flex: 0 0
    ${(props) =>
      props.headerMaxValueWidth ? props.headerMaxValueWidth : "140px"};

  @media screen and (min-width: 768px) {
    flex: 1;
    font-size: 14px;
    flex: 0 0
      ${(props) =>
        props.headerMaxValueWidth ? props.headerMaxValueWidth : "333px"};
  }
`;

const ListingLineContainer = styled.div<
  Pick<ListingProps, "headingColor"> & { alignItems?: string; gap?: string }
>`
  display: flex;
  align-items: ${(props) => (props.alignItems ? props.alignItems : "center")};
  justify-content: space-between;
  gap: ${(props) => props.gap || "24px"};
  ${ListingHeadingText} {
    color: ${(props) => (props.headingColor ? props.headingColor : "#434343")};
  }
  ${ListingHeadingValue} {
    color: ${(props) => (props.headingColor ? props.headingColor : "#434343")};
  }
`;

const ListingContainer = styled.div<Pick<ListingProps, "variant">>`
  display: flex;
  flex-direction: column;
  row-gap: ${(props) =>
    props.variant === "default"
      ? "26px"
      : props.variant === "summarization"
      ? "48px"
      : "0"};
  @media screen and (min-width: 768px) {
    row-gap: ${(props) =>
      props.variant === "default"
        ? "26px"
        : props.variant === "summarization"
        ? "48px"
        : "0"};
  }
`;

const ListingDisclaimerText = styled.p`
  color: #434343;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  width: 100%;
`;

const LoaderComponentClass = css`
  position: absolute;
  right: 0;
  top: -20px;
`;

const ListingSubTotal = styled.div<{ alignItems: string | undefined }>`
  align-items: ${(p) => p.alignItems || "flex-start"};
  display: flex;
  justify-content: space-between;
  margin: 15px auto;
  &:last-child {
    margin-top: 15px;
  }
`;

const ListingSubTotalLabel = styled.div`
  width: 50%;
`;

const SubtotalContainer = styled.div`
  margin-top: 10px;
`;
export interface Content {
  text: string;
  value?: string;
}

export interface Source {
  heading: Content;
  descriptions?: Content[];
  isPlanSummary?: number;
}
interface subTotal {
  label: string;
  value: string;
}
export interface ListingProps {
  gap?: string;
  variant: "default" | "summarization" | "alternate";
  subTotal?: subTotal[] | undefined;
  sources: Source[];
  theme?: ITheme;
  margin: string;
  marginItems: string | number;
  underlineColor?: string;
  headingColor?: string;
  summary: Content;
  isCalculating?: boolean;
  alignDescriptionItems?:
    | "stretch"
    | "center"
    | "flex-start"
    | "flex-end"
    | "baseline"
    | "initial"
    | "inherit";
  headerMaxValueWidth?: string;
  summaryTextColor?: string;
  summaryTextFontSize?: string;
  summaryValueFontSize?: string;
  headingFontSize?: string;
  dividerMarginTop?: string;
  dividerMarginBottom?: string;
  showDisclaimer?: boolean;
  disclaimerText?: string;
}

interface SummaryTextProps {
  fontSize?: string;
  summaryTextColor?: string;
}

interface DividerProps {
  dividerMarginTop?: string;
  dividerMarginBottom?: string;
  dividerBackground?: string;
}

function Listing({
  sources,
  subTotal,
  summary,
  variant,
  underlineColor,
  margin,
  marginItems,
  isCalculating,
  headingColor,
  alignDescriptionItems,
  headerMaxValueWidth,
  summaryTextColor,
  headingFontSize,
  summaryTextFontSize,
  summaryValueFontSize,
  dividerMarginTop,
  dividerMarginBottom,
  showDisclaimer,
  disclaimerText,
}: ListingProps) {
  const theme = useTheme();
  const CurrentPolicyFlow = useAvivaSelector(
    (state) => state.loading.CurrentPolicyFlow.policy
  );
  const isSheildStarter = CurrentPolicyFlow === PLANS_NAME.SHIELD_STARTER;
  return (
    <ListingLayout margin={margin} theme={theme}>
      <ListingContainer variant={variant}>
        {sources.map((item: Source, i: number) => (
          <>
            <ListingItemContainer
              key={i}
              underlineColor={underlineColor}
              variant={variant}
            >
              {item.heading.text && (
                <ListingLineContainer headingColor={headingColor}>
                  <ListingHeadingText
                    headingFontSize={headingFontSize}
                    variant={variant}
                  >
                    {item.heading.text}
                  </ListingHeadingText>
                  <ListingHeadingValue
                    headerMaxValueWidth={headerMaxValueWidth}
                    variant={variant}
                  >
                    {isCalculating ? (
                      <CalculatingLoader className={LoaderComponentClass} />
                    ) : (
                      item.heading.value
                    )}
                  </ListingHeadingValue>
                </ListingLineContainer>
              )}
              <ListingItemDescriptionContainer>
                <ul>
                  {item.descriptions &&
                    item.descriptions.map((description: Content, j: number) => (
                      <li
                        style={{
                          marginLeft: "24px",
                          marginTop: item?.isPlanSummary ? "14px" : "0px",
                        }}
                      >
                        <ListingLineContainer
                          key={j}
                          alignItems={alignDescriptionItems}
                        >
                          <ListingDescriptionText
                            dangerouslySetInnerHTML={{
                              __html: description.text,
                            }}
                          ></ListingDescriptionText>
                          <ListingDescriptionValue
                            variant={variant}
                            dangerouslySetInnerHTML={{
                              __html: `${description.value || ""}`,
                            }}
                          ></ListingDescriptionValue>
                        </ListingLineContainer>
                      </li>
                    ))}
                </ul>
              </ListingItemDescriptionContainer>
            </ListingItemContainer>
          </>
        ))}
        {showDisclaimer && (
          <ListingItemContainer
            key={"disclaimer"}
            underlineColor={underlineColor}
            variant={variant}
          >
            <ListingDisclaimerText>
              {disclaimerText || ""}
            </ListingDisclaimerText>
          </ListingItemContainer>
        )}
      </ListingContainer>
      {subTotal &&
        subTotal.map((items: subTotal, i) => {
          return (
            <SubtotalContainer>
              {i > 0 && <hr />}
              <ListingSubTotal
                key={"subtlt-" + i}
                alignItems={alignDescriptionItems}
              >
                <ListingDescriptionText
                  dangerouslySetInnerHTML={{ __html: items.label }}
                ></ListingDescriptionText>
                <ListingDescriptionValue
                  variant={variant}
                  dangerouslySetInnerHTML={{ __html: items.value }}
                ></ListingDescriptionValue>
              </ListingSubTotal>
            </SubtotalContainer>
          );
        })}
      {summary && (
        <>
          <ListingDidiver
            dividerMarginBottom={dividerMarginBottom}
            dividerMarginTop={isSheildStarter ? "0px" : dividerMarginTop}
            dividerBackground={isSheildStarter ? "#434343" : ""}
          ></ListingDidiver>
          <ListingLineContainer>
            <SummaryText
              fontSize={summaryTextFontSize}
              summaryTextColor={summaryTextColor}
            >
              {summary.text}
            </SummaryText>
            <SummaryText
              fontSize={summaryValueFontSize}
              summaryTextColor={
                isSheildStarter ? themeDefault.colors.primary : summaryTextColor
              }
            >
              {summary.value}
            </SummaryText>
          </ListingLineContainer>
        </>
      )}
    </ListingLayout>
  );
}

Listing.defaultProps = {
  variant: "default",
  sources: [],
  margin: "0 0 30px",
  marginItems: 0,
  summary: null,
  underlineColor: "",
};

export default Listing;
